/**
 * Модели из блоков на чистом CSS 3D — общий движок для острова главной вики, архипелага
 * режимов на лендинге и значков режимов.
 *
 * Текстуры 16×16 рисуются кодом при загрузке (свои, не из Minecraft) и сразу кодируются в BMP:
 * через canvas и toDataURL та же работа занимала десятки миллисекунд, а на телефоне — заметную
 * паузу. Затенение граней «запечено» в текстуры: верх светлее, боковые темнее. Создаются только
 * грани, которые можно увидеть: закрытые соседним блоком, нижние (камера всегда смотрит сверху)
 * и, у неподвижной камеры, отвёрнутые от неё — нет.
 *
 * Модель — набор абсолютно позиционированных граней с transform-style: preserve-3d; стили
 * граней и блоков — в css/voxel.css, размер блока задаёт CSS-переменная --b.
 */
import { el } from "./ui.js";

// ──────────────────────────── Текстуры ────────────────────────────

/** Затенение граней: верх, север/юг, запад/восток. */
const SHADES = { top: 1, side: 0.84, side2: 0.7 };

/** Детерминированный генератор случайных чисел — модели всегда выглядят одинаково. */
function seededRandom(seed) {
  let state = seed >>> 0;
  return () => {
    state = (state + 0x6d2b79f5) >>> 0;
    let value = Math.imul(state ^ (state >>> 15), 1 | state);
    value = (value + Math.imul(value ^ (value >>> 7), 61 | value)) ^ value;
    return ((value ^ (value >>> 14)) >>> 0) / 4294967296;
  };
}

const clamp = (value) => Math.max(0, Math.min(255, Math.round(value)));

/** Цвет с небольшим разбросом яркости — «шум» пиксельной текстуры. */
function vary(color, spread, random) {
  const shift = (random() - 0.5) * spread;
  return color.map((channel) => clamp(channel + shift));
}

/** Пиксели текстуры 16×16: paint(x, y, random) → [r, g, b]. */
function paintTexture(seed, paint) {
  const random = seededRandom(seed);
  const pixels = [];
  for (let y = 0; y < 16; y++) {
    for (let x = 0; x < 16; x++) pixels.push(paint(x, y, random));
  }
  return pixels;
}

const DIRT = [134, 96, 67];
const PLANK = [166, 116, 60];
const PLANK_EDGE = [74, 50, 26];
const PEEL = [109, 76, 42];
const WOOL_RED = [176, 46, 38];
const TNT_RED = [196, 52, 36];
const DARK_WOOD = [74, 52, 38];

function dirtPixel(random) {
  const roll = random();
  if (roll < 0.1) return [108, 76, 50];
  if (roll < 0.18) return [156, 116, 82];
  return vary(DIRT, 14, random);
}

/** Буквы «TNT» шрифтом 3×5 и 4×5: строки по пять пикселей сверху вниз. */
const TNT_LETTERS = [
  { x: 2, rows: ["###", ".#.", ".#.", ".#.", ".#."] },
  { x: 6, rows: ["#..#", "##.#", "#.##", "#..#", "#..#"] },
  { x: 11, rows: ["###", ".#.", ".#.", ".#.", ".#."] },
];

/** Пиксель надписи «TNT» на белой полосе (y — строка полосы от 0 до 4). */
function tntLetter(x, y) {
  return TNT_LETTERS.some((letter) => letter.rows[y] && letter.rows[y][x - letter.x] === "#");
}

/**
 * Булыжник: камни неправильной формы и тёмные швы между ними. Центры камней — фиксированные
 * точки, шов — там, где пиксель почти одинаково близок к двум центрам.
 */
const COBBLE_CENTERS = [[2, 2], [8, 1], [13, 3], [4, 7], [10, 7], [15, 9], [1, 12], [6, 13], [12, 13]];

function cobblePixel(x, y, random) {
  const distances = COBBLE_CENTERS
    .map(([centerX, centerY]) => Math.hypot(Math.min(Math.abs(x - centerX), 16 - Math.abs(x - centerX)), Math.min(Math.abs(y - centerY), 16 - Math.abs(y - centerY))))
    .sort((a, b) => a - b);
  if (distances[1] - distances[0] < 1) return vary([70, 70, 70], 8, random);
  return vary(distances[0] < 1.5 ? [150, 150, 150] : [122, 122, 122], 18, random);
}

/** Рисовальщики текстур: имя → пиксели. */
const TEXTURES = {
  grassTop: () => paintTexture(11, (x, y, random) => {
    const roll = random();
    if (roll < 0.1) return [72, 128, 42];
    if (roll < 0.18) return [124, 184, 72];
    return vary([98, 158, 56], 16, random);
  }),
  grassSide: () => paintTexture(12, (x, y, random) => {
    // Неровный край травы на боковой грани.
    const edge = 3 + ((x * 5 + 2) % 4 === 0 ? 1 : 0) - ((x * 3) % 7 === 0 ? 1 : 0);
    return y < edge ? vary([96, 156, 54], 16, random) : dirtPixel(random);
  }),
  dirt: () => paintTexture(13, (x, y, random) => dirtPixel(random)),
  logSide: () => paintTexture(14, (x, y, random) => vary((x + (y >> 2)) % 4 === 0 ? [80, 60, 36] : [106, 82, 51], 12, random)),
  logTop: () => paintTexture(15, (x, y, random) => {
    const ring = Math.max(Math.abs(x - 7.5), Math.abs(y - 7.5));
    if (ring > 6.5) return vary([88, 66, 40], 10, random);
    return vary(Math.round(ring) % 2 ? [176, 140, 88] : [150, 118, 72], 10, random);
  }),
  leaves: () => paintTexture(16, (x, y, random) => (random() < 0.12 ? [34, 80, 26] : vary([60, 126, 44], 26, random))),
  chestSide: () => paintTexture(17, (x, y, random) =>
    (x === 0 || x === 15 || y === 0 || y === 15 || y === 5 ? PLANK_EDGE : vary(PLANK, 14, random))),
  chestFront: () => paintTexture(18, (x, y, random) => {
    if (x >= 7 && x <= 8 && y >= 3 && y <= 7) return y === 3 || y === 7 ? [120, 120, 112] : [206, 206, 196];
    return x === 0 || x === 15 || y === 0 || y === 15 || y === 5 ? PLANK_EDGE : vary(PLANK, 14, random);
  }),
  chestTop: () => paintTexture(19, (x, y, random) => (x === 0 || x === 15 || y === 0 || y === 15 ? PLANK_EDGE : vary([176, 126, 68], 14, random))),
  kiwiSide: () => paintTexture(20, (x, y, random) => (random() < 0.2 ? [140, 106, 66] : vary(PEEL, 14, random))),
  kiwiTop: () => paintTexture(21, (x, y, random) => {
    // Срез киви: кожура, яркая мякоть, кольцо семечек и светлая сердцевина.
    const distance = Math.hypot(x - 7.5, y - 7.5);
    if (distance > 7.2) return vary(PEEL, 10, random);
    if (distance > 6.1) return [128, 186, 64];
    if (distance < 1.7) return [236, 242, 214];
    const angle = Math.atan2(y - 7.5, x - 7.5);
    const seed = distance > 3.2 && distance < 4.8 && Math.round((angle + Math.PI) * 2.2) % 2 === 0;
    return seed ? [30, 24, 16] : vary([164, 208, 96], 14, random);
  }),
  cobble: () => paintTexture(30, cobblePixel),
  netherrack: () => paintTexture(31, (x, y, random) => {
    const roll = random();
    if (roll < 0.14) return [74, 22, 24];
    if (roll < 0.26) return [146, 64, 58];
    return vary([112, 38, 38], 16, random);
  }),
  tntSide: () => paintTexture(32, (x, y, random) => {
    // Красные бока с тёмными полосами шашек и белая полоса с надписью посередине.
    if (y >= 5 && y <= 11) {
      if (y === 5 || y === 11) return [222, 222, 214];
      return tntLetter(x, y - 6) ? [32, 32, 32] : vary([236, 236, 228], 6, random);
    }
    return x % 4 === 0 ? vary([140, 34, 24], 8, random) : vary(TNT_RED, 14, random);
  }),
  tntTop: () => paintTexture(33, (x, y, random) => {
    const distance = Math.hypot(x - 7.5, y - 7.5);
    if (distance < 1.6) return [40, 36, 32];
    if (distance < 3) return vary([220, 214, 200], 8, random);
    return vary(TNT_RED, 16, random);
  }),
  woolRed: () => paintTexture(34, (x, y, random) => vary((x + y * 2) % 5 === 0 ? [150, 36, 30] : WOOL_RED, 14, random)),
  bedHead: () => paintTexture(35, (x, y, random) => {
    // Подушка у изголовья и красное одеяло.
    if (y <= 6) return x === 0 || x === 15 || y === 0 || y === 6 ? [196, 196, 188] : vary([238, 238, 232], 6, random);
    return vary(WOOL_RED, 12, random);
  }),
  bedFoot: () => paintTexture(36, (x, y, random) => (y === 15 ? [130, 30, 26] : vary(WOOL_RED, 12, random))),
  bedSide: () => paintTexture(37, (x, y, random) => {
    // Одеяло сверху, деревянная рама снизу; при сжатии в полблока пропорции как у кровати.
    if (y <= 8) return vary(WOOL_RED, 12, random);
    if (y <= 10) return vary([226, 226, 218], 6, random);
    return vary(PLANK, 14, random);
  }),
  gold: () => paintTexture(38, (x, y, random) => {
    // Золотой блок: светлая фаска сверху и слева, тёмная снизу и справа.
    if (x === 0 || y === 0) return [255, 246, 156];
    if (x === 15 || y === 15) return [196, 140, 30];
    return vary([246, 208, 62], 18, random);
  }),
  bricks: () => paintTexture(39, (x, y, random) => {
    const row = Math.floor(y / 4);
    const joint = (x + (row % 2) * 4) % 8 === 0;
    if (y % 4 === 3 || joint) return vary([190, 182, 170], 8, random);
    return vary([150, 72, 56], 18, random);
  }),
  target: () => paintTexture(40, (x, y, random) => {
    // Мишень: красные и белые кольца — значок испытаний на реакцию.
    const ring = Math.max(Math.abs(x - 7.5), Math.abs(y - 7.5));
    const red = ring > 6.5 || (ring > 3.5 && ring <= 5.5) || ring <= 1.5;
    return red ? vary([200, 48, 44], 12, random) : vary([236, 230, 220], 6, random);
  }),
  jukeboxSide: () => paintTexture(41, (x, y, random) =>
    (x === 0 || x === 15 || y === 0 || y === 15 ? [46, 32, 24] : vary(DARK_WOOD, 14, random))),
  jukeboxTop: () => paintTexture(42, (x, y, random) => {
    // Щель для пластинки посередине крышки.
    if (y >= 7 && y <= 8 && x >= 3 && x <= 12) return [20, 16, 14];
    return x === 0 || x === 15 || y === 0 || y === 15 ? [46, 32, 24] : vary([92, 66, 48], 14, random);
  }),
};

/**
 * Картинка 16×16 в формате BMP (data URL) с затенением light. Собирается байтами прямо в JS,
 * без canvas.
 */
function bitmapUrl(pixels, light) {
  const size = 16;
  const dataSize = size * size * 3;
  const bytes = new Uint8Array(54 + dataSize);
  const header = new DataView(bytes.buffer);
  bytes[0] = 0x42; // «BM»
  bytes[1] = 0x4d;
  header.setUint32(2, bytes.length, true);
  header.setUint32(10, 54, true); // где начинаются пиксели
  header.setUint32(14, 40, true); // размер заголовка BITMAPINFOHEADER
  header.setInt32(18, size, true);
  header.setInt32(22, -size, true); // отрицательная высота — строки идут сверху вниз
  header.setUint16(26, 1, true);
  header.setUint16(28, 24, true); // 24 бита на пиксель, без сжатия
  header.setUint32(34, dataSize, true);
  pixels.forEach(([red, green, blue], index) => {
    const offset = 54 + index * 3; // BMP хранит цвет как синий, зелёный, красный
    bytes[offset] = clamp(blue * light);
    bytes[offset + 1] = clamp(green * light);
    bytes[offset + 2] = clamp(red * light);
  });
  let binary = "";
  for (const byte of bytes) binary += String.fromCharCode(byte);
  return `data:image/bmp;base64,${btoa(binary)}`;
}

/** Нарисованные текстуры и уже добавленные на страницу правила («текстура-затенение»). */
const painted = new Map();
const injected = new Set();
let textureSheet = null;

/**
 * Добавить на страницу CSS-правила текстур граней (класс isl-<текстура>-<затенение>).
 * Каждое сочетание кодируется один раз на страницу, сколько бы моделей его ни использовали.
 */
function ensureTextures(used) {
  const rules = [];
  for (const key of used) {
    if (injected.has(key)) continue;
    injected.add(key);
    const [name, shade] = key.split("-");
    if (!painted.has(name)) painted.set(name, TEXTURES[name]());
    rules.push(`.isl-${key}{background-image:url(${bitmapUrl(painted.get(name), SHADES[shade])})}`);
  }
  if (!rules.length) return;
  if (!textureSheet) {
    textureSheet = el("style");
    document.head.appendChild(textureSheet);
  }
  textureSheet.append(rules.join(""));
}

// ──────────────────────────── Кубики ────────────────────────────

/**
 * Виды блоков: текстуры верхней и боковых граней (south — своя передняя, как у сундука)
 * и форма. Форма small — сундук чуть меньше блока, bed — кровать высотой в полблока.
 * Неполные блоки не прячут грани соседей и сами всегда видны целиком.
 */
const KINDS = {
  grass: { top: "grassTop", side: "grassSide" },
  dirt: { top: "dirt", side: "dirt" },
  log: { top: "logTop", side: "logSide" },
  leaves: { top: "leaves", side: "leaves" },
  chest: { top: "chestTop", side: "chestSide", south: "chestFront", shape: "small" },
  kiwi: { top: "kiwiTop", side: "kiwiSide" },
  cobble: { top: "cobble", side: "cobble" },
  netherrack: { top: "netherrack", side: "netherrack" },
  tnt: { top: "tntTop", side: "tntSide" },
  wool: { top: "woolRed", side: "woolRed" },
  bedHead: { top: "bedHead", side: "bedSide", shape: "bed" },
  bedFoot: { top: "bedFoot", side: "bedSide", shape: "bed" },
  gold: { top: "gold", side: "gold" },
  bricks: { top: "bricks", side: "bricks" },
  target: { top: "target", side: "target" },
  jukebox: { top: "jukeboxTop", side: "jukeboxSide" },
};

const isFull = (kind) => !KINDS[kind].shape;

/** Грани куба: направление к соседу, поворот грани и её затенение. Нижних граней нет. */
const FACES = {
  top: { offset: [0, 1, 0], rotate: "rotateX(90deg)", shade: "top" },
  south: { offset: [0, 0, 1], rotate: "", shade: "side" },
  north: { offset: [0, 0, -1], rotate: "rotateY(180deg)", shade: "side" },
  east: { offset: [1, 0, 0], rotate: "rotateY(90deg)", shade: "side2" },
  west: { offset: [-1, 0, 0], rotate: "rotateY(-90deg)", shade: "side2" },
};

/**
 * Грани, видимые неподвижной камерой, которая смотрит сверху с юго-востока
 * (rotateX(-24…-30deg) rotateY(-30…-45deg)): верх, юг и восток.
 */
export const FIXED_VIEW_FACES = ["top", "south", "east"];

/**
 * Блоки модели для CSS 3D.
 *
 * @param {Array<[number, number, number, string]>} blocks  [x, y, z, вид]; y — высота
 *        (0 — верхний слой земли), z растёт к зрителю
 * @param {object} options
 * @param {{x: number, y: number, z: number}} options.center  точка модели, которая встанет в начало координат
 * @param {string[]} [options.faces]  грани, которые камера может увидеть (у вращающейся модели — все)
 * @param {((index: number) => number)|null} [options.dropDelay]  задержка падения блока в мс
 *        для сборки модели из падающих блоков; null — модель сразу на месте
 * @returns {HTMLElement[]} блоки снизу вверх — в этом порядке они и падают при сборке
 */
export function buildBlocks(blocks, { center, faces = Object.keys(FACES), dropDelay = null }) {
  const occupied = new Map(blocks.map(([x, y, z, kind]) => [`${x},${y},${z}`, kind]));
  const used = new Set();
  const built = [];

  for (const [x, y, z, kind] of blocks) {
    const spec = KINDS[kind];
    const nodes = [];
    for (const name of faces) {
      const face = FACES[name];
      const neighbour = occupied.get(`${x + face.offset[0]},${y + face.offset[1]},${z + face.offset[2]}`);
      if (neighbour && isFull(neighbour) && isFull(kind)) continue;
      const texture = `${spec[name] || spec.side}-${face.shade}`;
      used.add(texture);
      nodes.push(el("div", {
        class: `isl__face isl-${texture}`,
        // Лёгкое увеличение грани закрывает сглаженные щели на стыках кубиков.
        style: `transform:${face.rotate} translateZ(calc(var(--b) / 2)) scale(1.02)`,
      }));
    }
    if (nodes.length) built.push({ x, y, z, shape: spec.shape, nodes, order: y * 10 + x + z });
  }
  ensureTextures(used);

  built.sort((a, b) => a.order - b.order);
  return built.map((block, index) => el("div", {
    class: `isl__block${block.shape ? ` isl__block--${block.shape}` : ""}`,
    style: `--x:${block.x - center.x};--y:${-(block.y - center.y)};--z:${block.z - center.z}`,
  }, dropDelay ? el("div", { class: "isl__drop", style: `--delay:${dropDelay(index)}ms` }, block.nodes) : block.nodes));
}

/**
 * Значок из одного блока (вид kind) в изометрии — для карточек и списков. Декоративный:
 * подпись рядом всегда есть текстом.
 */
export function cubeIcon(kind) {
  return el("span", { class: "vx-icon", "aria-hidden": "true" },
    el("span", { class: "vx-icon__view" }, buildBlocks([[0, 0, 0, kind]], { center: { x: 0, y: 0, z: 0 }, faces: FIXED_VIEW_FACES })));
}

/**
 * Наклон модели за курсором: плавно догоняет курсор и пишет углы в CSS-переменные
 * --tilt-x и --tilt-y элемента tilt. Возвращает функцию отписки.
 */
export function followPointer(stage, tilt, { yaw = 26, pitch = 12 } = {}) {
  let targetX = 0;
  let targetY = 0;
  let currentX = 0;
  let currentY = 0;
  let frame = 0;
  const tick = () => {
    currentX += (targetX - currentX) * 0.08;
    currentY += (targetY - currentY) * 0.08;
    tilt.style.setProperty("--tilt-x", `${currentX.toFixed(2)}deg`);
    tilt.style.setProperty("--tilt-y", `${currentY.toFixed(2)}deg`);
    frame = Math.abs(targetX - currentX) + Math.abs(targetY - currentY) > 0.05 ? requestAnimationFrame(tick) : 0;
  };
  const onMove = (event) => {
    const box = stage.getBoundingClientRect();
    targetY = ((event.clientX - (box.left + box.width / 2)) / window.innerWidth) * yaw;
    targetX = -((event.clientY - (box.top + box.height / 2)) / window.innerHeight) * pitch;
    if (!frame) frame = requestAnimationFrame(tick);
  };
  window.addEventListener("pointermove", onMove, { passive: true });
  return () => {
    window.removeEventListener("pointermove", onMove);
    cancelAnimationFrame(frame);
  };
}
